import { useStoreAccess } from "@/shared/access/StoreAccess";
import { useMemo } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { getSellerPath } from "@/lib/routing/portal-urls";
import { useSessionQuery } from "@/shared/services/useSessionQuery";
import { useStoreQuery } from "@/shared/services/useStoreQuery";
import {
  SHARE_READINESS_IDS,
  resolveShareReadiness,
  type ShareReadinessId,
} from "@/shared/lib/store-readiness";

export type ShareReadinessItem = {
  id: ShareReadinessId;
  title: string;
  href: string;
  done: boolean;
};

export type ShareReadiness = {
  items: ShareReadinessItem[];
  /** How many of the five a cold buyer would miss. */
  /**
   * Both sources have answered. Until they have, every check reads as NOT done
   * — it is built from absent data — so a finished store would flash "5 things
   * to check" for a beat on a cold open. Same gate as the dashboard guide, and
   * for the same reason.
   */
  ready: boolean;
};

/**
 * What a stranger arriving cold from a shared link would find missing.
 *
 * Reads the session overview + the store record — BOTH already mounted by
 * `AppShell`, so this costs no extra network call, the same trick
 * `useDashboardSetupGuide` uses. The rules themselves live in
 * `shared/lib/store-readiness.ts`, shared with the dashboard checklist so the
 * two surfaces cannot disagree about whether a COD-only store can take money.
 *
 * Staleness: the catalogue and payment invalidators already refresh
 * `queryKeys.seller.session`, and the policy/branding mutations refresh
 * `queryKeys.seller.store`, so a merchant who fixes a row and reopens the sheet
 * sees it gone.
 */
export function useShareReadiness({
  storeSlug,
  locale,
}: {
  storeSlug: string;
  locale: string;
}): ShareReadiness {
  const { access } = useStoreAccess();
  const t = useTranslations();
  const { data: sessionData } = useSessionQuery({ storeSlug });
  const { data: storeData } = useStoreQuery({ storeSlug });

  const overview = sessionData?.overview ?? null;
  const ready = Boolean(sessionData) && Boolean(storeData);

  const productsHref = `${getSellerPath(locale, storeSlug, "/products")}?guide=add`;
  const paymentHref = `${getSellerPath(locale, storeSlug, "/settings")}?tab=payment&section=payment&guide=payment`;
  const contactHref = `${getSellerPath(locale, storeSlug, "/settings")}?tab=branding&section=branding&guide=contact`;
  // The link card has no setup-guide step of its own (it is deliberately not
  // counted in branding progress — a derived card is a working card), so it
  // gets its own spotlight anchor rather than landing the seller on a page of
  // six cards with nothing pointed at.
  const shareCardHref = `${getSellerPath(locale, storeSlug, "/settings")}?tab=branding&section=branding&guide=sharecard`;
  const policyHref = `${getSellerPath(locale, storeSlug, "/settings")}?tab=policies&section=policies&guide=policies`;

  const productsTitle = t(keys.share.readinessProducts);
  const paymentTitle = t(keys.share.readinessPayment);
  const contactTitle = t(keys.share.readinessContact);
  const shareCardTitle = t(keys.share.readinessShareCard);
  const policyTitle = t(keys.share.readinessPolicy);

  const enabledPolicyCount = (storeData?.storePolicies ?? []).filter(
    (policy) => policy.isEnabled,
  ).length;

  return useMemo<ShareReadiness>(() => {
    const done = resolveShareReadiness({
      hasProducts: overview?.hasProducts === true,
      hasActivePaymentOptions: overview?.hasActivePaymentOptions,
      acceptsCod: storeData?.acceptsCod,
      whatsappNumber: storeData?.whatsappNumber,
      shareCard: storeData?.shareCard,
      enabledPolicyCount,
    });
    // Copy and destination per id, so the rendered list is SHARE_READINESS_IDS
    // .map(...) and a new row cannot appear without joining the pinned list.
    const rowCopy: Record<ShareReadinessId, { title: string; href: string }> = {
      products: { title: productsTitle, href: productsHref },
      payment: { title: paymentTitle, href: paymentHref },
      contact: { title: contactTitle, href: contactHref },
      shareCard: { title: shareCardTitle, href: shareCardHref },
      policy: { title: policyTitle, href: policyHref },
    };
    const items: ShareReadinessItem[] = SHARE_READINESS_IDS.filter(id => id === "products" || (access?.capabilities.profileBranding === true && (id !== "payment" || access.capabilities.manualPayments))).map((id) => ({
      id,
      title: rowCopy[id].title,
      href: rowCopy[id].href,
      done: done[id],
    }));
    return {
      items,
      ready,
    };
  }, [
    access,
    ready,
    enabledPolicyCount,
    overview?.hasProducts,
    overview?.hasActivePaymentOptions,
    storeData?.acceptsCod,
    storeData?.whatsappNumber,
    storeData?.shareCard,
    productsTitle,
    paymentTitle,
    contactTitle,
    shareCardTitle,
    policyTitle,
    productsHref,
    paymentHref,
    contactHref,
    shareCardHref,
    policyHref,
  ]);
}
